import { ShieldCheck, Shield, ExternalLink } from "lucide-react";
import { fmtUsd } from "../nl";
import { entityTrack } from "@/lib/entities";
import { ModuleShell, ModuleDelayed, StatusBadge, UpdatedStamp, INNER_CARD, fmtDate } from "./shell";

/* 02 — Security & Risk (approved frame). Verified incidents only —
   deterministic entity matching, calm forensic tone, no safety score. */

const HACKS_URL = "https://defillama.com/hacks";

const incidentStatus = (inc: any): { label: string; color: string } => {
  const amt = inc.amount_usd || 0;
  const ret = inc.returned_usd || 0;
  if (amt > 0 && ret >= amt) return { label: "FUNDS RETURNED", color: "#1fe09c" };
  if (ret > 0) return { label: "PARTIAL RETURN", color: "#22d3ee" };
  return { label: "RECORDED", color: "#9499ad" };
};

const IncidentCard = ({ inc, entityId, testid }: any) => {
  const st = incidentStatus(inc);
  const cls = [inc.classification, inc.target_type].filter(Boolean).join(" · ");
  return (
    <div className="relative pl-[22px] pb-[18px] last:pb-0" data-testid={testid}>
      <span aria-hidden className="absolute left-[3px] top-[10px] bottom-0 w-px bg-[#1a1c2b]" />
      <span aria-hidden className="absolute left-0 top-[6px] h-[7px] w-[7px] rounded-full border-[1.5px] border-[#22d3ee] bg-[#030405]" />
      <p className="nl-mono text-[9px] leading-[13px] font-medium uppercase text-[#9499ad]">{fmtDate(inc.date)}</p>
      <div className={`${INNER_CARD} mt-[7px] px-[14px] py-[13px] md:px-[16px] md:py-[14px]`}>
        <div className="flex flex-wrap items-start justify-between gap-x-[12px] gap-y-[7px]">
          <p className="nl-geist text-[13px] md:text-[13.5px] leading-[18px] font-semibold text-[#f6f7fc] min-w-0">
            {inc.name}{inc.classification ? ` — ${inc.classification}` : ""}
          </p>
          <span className="flex items-center gap-[6px]">
            {inc.scope === "third_party" && (
              <StatusBadge label="THIRD-PARTY EVENT" color="#9499ad" testid={`${testid}-scope`} />
            )}
            <StatusBadge label={st.label} color={st.color} testid={`${testid}-status`} />
          </span>
        </div>
        {inc.scope === "third_party" && (
          <p className="nl-geist text-[11px] md:text-[11.5px] leading-[16px] text-[#9499ad] mt-[6px]" data-testid={`${testid}-scope-note`}>
            Ecosystem event affecting users via third-party surfaces — not an exploit of the
            protocol&apos;s contracts.
          </p>
        )}
        {inc.technique && (
          <p className="nl-geist text-[11.5px] md:text-[12px] leading-[17px] text-[#9499ad] mt-[7px]">
            {inc.technique}
          </p>
        )}
        <p className="nl-geist text-[11.5px] md:text-[12px] leading-[16px] mt-[9px]">
          <span className="text-[#b9b9c7]">Impact: <span className="text-[#f6f7fc] font-semibold">{fmtUsd(inc.amount_usd)}</span></span>
          {(inc.returned_usd || 0) > 0 && (
            <span className="text-[#1fe09c] font-semibold"> &nbsp;|&nbsp; {fmtUsd(inc.returned_usd)} returned</span>
          )}
        </p>
        <a href={HACKS_URL} target="_blank" rel="noopener noreferrer"
          onClick={() => entityTrack("security_incident_click", entityId, { incident: inc.name, date: inc.date })}
          className="nl-mono inline-flex items-center gap-[5px] text-[8px] md:text-[9px] font-semibold uppercase text-[#9499ad] hover:text-[#22d3ee] transition-colors mt-[10px]">
          Source: DeFiLlama Hacks <ExternalLink className="h-[10px] w-[10px]" />
        </a>
      </div>
    </div>
  );
};

export const EntitySecurityModule = ({ overview }: { overview: any }) => {
  const sec = overview.security;
  const name = overview.identity.name;
  if (!sec) return null;
  if (sec.error)
    return <ModuleDelayed num="2" title="Security & Risk" sub="Verified Incidents" testid="entity-security-delayed"
      message="Security data is temporarily delayed — the incident provider could not be reached. Nothing is estimated in its place." />;

  const incidents = sec.incidents || [];
  const recentIncident = incidents.some((i: any) => i.date && Date.now() - new Date(i.date).getTime() < 30 * 86400000);
  const entityId = overview.identity.entity_id;

  return (
    <ModuleShell num="2" title="Security & Risk" sub="Verified Incidents" testid="entity-security"
      right={
        <div className={`${INNER_CARD} flex items-center gap-[10px] px-[13px] py-[9px]`} data-testid="security-current-state">
          <ShieldCheck aria-hidden className="h-[16px] w-[16px]" style={{ color: recentIncident ? "#22d3ee" : "#1fe09c" }} />
          <div>
            <p className="nl-mono text-[7.5px] leading-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--nl-text-faint)]">Current State</p>
            <p className="nl-geist text-[11px] leading-[14px] font-semibold text-[#f6f7fc] mt-[1px]">
              {recentIncident ? "Recent verified incident — see timeline" : "No active verified incidents"}
            </p>
          </div>
        </div>
      }
      footNote={
        <span className="inline-flex items-start gap-[7px]">
          <Shield aria-hidden className="h-[12px] w-[12px] shrink-0 mt-[1px] text-[#9499ad]" />
          <span data-testid="security-disclosure">
            Incidents are verified by NewsLayer through deterministic entity matching and structured
            source evidence. No incidents are inferred.
          </span>
        </span>
      }
      footRight={<UpdatedStamp iso={sec.fetched_at} testid="security-updated" />}>
      <p className="sr-only" data-testid="security-sr-summary">
        {recentIncident
          ? `${name} has a recently verified security incident in the NewsLayer dataset.`
          : `${name} has no currently active verified security incidents in the NewsLayer dataset.`}
        {" "}{incidents.length} historical incident{incidents.length === 1 ? " is" : "s are"} listed below.
      </p>

      <div className="grid lg:grid-cols-[minmax(0,64fr)_minmax(0,36fr)] gap-[20px] lg:gap-[28px]">
        <div>
          <p className="nl-geist text-[10px] md:text-[11px] leading-[14px] font-semibold tracking-[0.06em] uppercase text-[#f6f7fc]">
            Incident Timeline
          </p>
          <div className="mt-[14px]">
            {incidents.length ? incidents.map((inc: any, i: number) => (
              <IncidentCard key={i} inc={inc} entityId={entityId} testid={`security-incident-${i}`} />
            )) : (
              <p className="nl-geist text-[12.5px] leading-[18px] text-[#9499ad]" data-testid="security-empty">
                No verified {name} incidents recorded by the provider. Absence of recorded incidents
                is not a safety certification.
              </p>
            )}
          </div>
        </div>
        <div className={`${INNER_CARD} px-[16px] py-[15px] self-start`} data-testid="security-about">
          <p className="nl-geist text-[10px] md:text-[11px] leading-[14px] font-semibold tracking-[0.06em] uppercase text-[#f6f7fc]">
            About This Data
          </p>
          <ul className="mt-[11px] space-y-[8px]">
            {[`Directly linked to ${name}`, "Verified by structured sources", "Deterministic matching rules",
              "No inferred or related protocol incidents included",
              ...(incidents.some((i: any) => i.scope === "third_party")
                ? ["Third-party events are classified by scope — they are not protocol contract exploits"]
                : [])].map((t, i) => (
              <li key={i} className="nl-geist text-[11.5px] leading-[16px] text-[#9499ad] flex gap-[8px]">
                <span aria-hidden className="text-[#22d3ee] mt-[1px]">•</span>{t}
              </li>
            ))}
          </ul>
          <div className="border-t border-[#14161f] mt-[14px] pt-[12px] space-y-[7px]">
            <a href={HACKS_URL} target="_blank" rel="noopener noreferrer"
              className="nl-mono flex items-center gap-[5px] text-[8px] md:text-[9px] font-semibold uppercase text-[#9499ad] hover:text-[#22d3ee] transition-colors">
              Source: DeFiLlama Hacks <ExternalLink className="h-[10px] w-[10px]" />
            </a>
            <UpdatedStamp iso={sec.fetched_at} />
          </div>
        </div>
      </div>
    </ModuleShell>
  );
};
